/**
 * Analytics taxonomy + the pure rules that turn API traffic into events.
 * No imports on purpose: this file is unit-tested with plain `node --test`
 * (frontend/src/lib/analyticsRules.test.js). See lib/analytics.js for the
 * runtime side and docs/specs/GAP_CLOSING_PLAN.md §M0.2 for the taxonomy.
 */

/** The taxonomy. Keep in sync with GAP_CLOSING_PLAN.md §M0.2. */
export const EVENTS = Object.freeze({
  SIGNED_UP: 'signed_up',
  WORKSPACE_CREATED: 'workspace_created',
  FILM_CREATED: 'film_created',
  SCRIPT_UPLOADED: 'script_uploaded',
  AI_BREAKDOWN_VIEWED: 'ai_breakdown_viewed',
  SCENES_IMPORTED: 'scenes_imported',
  SCENE_CREATED: 'scene_created',
  SHOT_CREATED: 'shot_created',
  SCHEDULE_CREATED: 'schedule_created',
  MEMBER_INVITED: 'member_invited',
  UPGRADE_MODAL_OPENED: 'upgrade_modal_opened',
  CHECKOUT_STARTED: 'checkout_started',
  CHECKOUT_COMPLETED: 'checkout_completed',
  TRIAL_STARTED: 'trial_started',
  AI_CAP_HIT: 'ai_cap_hit',
  PDF_EXPORT_CLICKED: 'pdf_export_clicked',
  PDF_EXPORT_FAILED: 'pdf_export_failed',
  SAMPLE_FILM_DELETED: 'sample_film_deleted',
  ELEMENTS_EDITED: 'elements_edited',
  SHOT_STATUS_UPDATED: 'shot_status_updated',
});
export const KNOWN_EVENTS = new Set(Object.values(EVENTS));

/* -------------------------------------------------------------------------
 * API-derived events
 * ---------------------------------------------------------------------- */

const ID = '[^/?#]+';
const rule = (method, pattern, event, props) => ({
  method, event, props, re: new RegExp(`/api${pattern}/?(?:\\?.*)?$`),
});

/**
 * Exported for unit testing. `props(match, responseData)` must return only
 * ids, counts and enums — never free text.
 */
export const API_EVENT_RULES = [
  rule('post', '/workspaces', EVENTS.WORKSPACE_CREATED, (_m, d) => ({ workspace_id: d?.id, plan_id: d?.plan_id })),
  rule('post', '/films', EVENTS.FILM_CREATED, (_m, d) => ({ film_id: d?.id })),
  rule('post', `/workspaces/(${ID})/films`, EVENTS.FILM_CREATED, (m, d) => ({ workspace_id: m[1], film_id: d?.id })),
  rule('post', `/films/(${ID})/script/parse`, EVENTS.SCRIPT_UPLOADED,
    (m, d) => ({ film_id: m[1], mode: 'rules', scene_count: d?.scenes?.length })),
  rule('post', `/films/(${ID})/script/ai-parse`, EVENTS.SCRIPT_UPLOADED,
    (m, d) => ({ film_id: m[1], mode: 'ai', scene_count: d?.scenes?.length, locked_count: d?.locked_count ?? 0 })),
  rule('post', `/films/(${ID})/script/import`, EVENTS.SCENES_IMPORTED,
    (m, d) => ({ film_id: m[1], scene_count: d?.created_count, skipped_count: d?.skipped_count })),
  rule('post', `/films/(${ID})/scenes`, EVENTS.SCENE_CREATED, (m) => ({ film_id: m[1] })),
  rule('post', `/scenes/(${ID})/shots`, EVENTS.SHOT_CREATED, (m) => ({ scene_id: m[1] })),
  rule('post', `/films/(${ID})/schedules`, EVENTS.SCHEDULE_CREATED, (m) => ({ film_id: m[1] })),
  rule('post', `/workspaces/(${ID})/invitations`, EVENTS.MEMBER_INVITED, (m, d) => ({ workspace_id: m[1], role: d?.role })),
  rule('post', `/films/(${ID})/invitations`, EVENTS.MEMBER_INVITED, (m, d) => ({ film_id: m[1], role: d?.role })),
  rule('post', `/workspaces/(${ID})/trial/start`, EVENTS.TRIAL_STARTED, (m) => ({ workspace_id: m[1] })),
  rule('post', `/workspaces/(${ID})/upgrade/create-subscription`, EVENTS.CHECKOUT_STARTED, (m) => ({ workspace_id: m[1], kind: 'plan' })),
  rule('post', `/workspaces/(${ID})/upgrade/verify-subscription`, EVENTS.CHECKOUT_COMPLETED, (m) => ({ workspace_id: m[1], kind: 'plan' })),
  rule('post', '/razorpay/workspace/create-subscription', EVENTS.CHECKOUT_STARTED, () => ({ kind: 'new_workspace' })),
  rule('post', '/razorpay/workspace/verify-subscription', EVENTS.CHECKOUT_COMPLETED, () => ({ kind: 'new_workspace' })),
  rule('post', `/workspaces/(${ID})/addons/(${ID})/subscribe`, EVENTS.CHECKOUT_STARTED, (m) => ({ workspace_id: m[1], kind: 'addon', addon_type: m[2] })),
  rule('post', `/workspaces/(${ID})/addons/verify`, EVENTS.CHECKOUT_COMPLETED, (m) => ({ workspace_id: m[1], kind: 'addon' })),
  rule('post', `/workspaces/(${ID})/credits/topup/create-order`, EVENTS.CHECKOUT_STARTED, (m) => ({ workspace_id: m[1], kind: 'ai_credits' })),
  rule('post', `/workspaces/(${ID})/credits/topup/verify`, EVENTS.CHECKOUT_COMPLETED, (m) => ({ workspace_id: m[1], kind: 'ai_credits' })),
];

/** Pure: (method, url, data) → [{event, properties}]. Exported for tests. */
export function eventsForResponse(method, url, data) {
  const m = String(method || '').toLowerCase();
  const out = [];
  // Several endpoints answer 200 with {success:false, error} — that is not a win.
  if (data && data.success === false) return out;
  for (const r of API_EVENT_RULES) {
    if (r.method !== m) continue;
    const match = r.re.exec(url || '');
    if (!match) continue;
    out.push({ event: r.event, properties: clean(r.props(match, data)) });
    break; // first rule wins — rules are ordered most-specific-safe
  }
  // The AI parse is also the moment the user first sees a breakdown, and the
  // moment a Free user meets the 10-scene cap.
  if (m === 'post' && /\/script\/ai-parse\/?(?:\?.*)?$/.test(url || '')) {
    const total = data?.scenes?.length ?? 0;
    const locked = data?.locked_count ?? 0;
    out.push({ event: EVENTS.AI_BREAKDOWN_VIEWED, properties: { scene_count: total, locked_count: locked } });
    if (locked > 0) out.push({ event: EVENTS.AI_CAP_HIT, properties: { cap: 'free_scene_cap', locked_count: locked } });
  }
  return out;
}

/** Pure: error response → [{event, properties}]. Exported for tests. */
export function eventsForError(status, data) {
  const code = data?.detail?.error || data?.error;
  if (status === 429 && (code === 'ai_credits_exhausted' || code === 'ai_daily_limit_exceeded')) {
    return [{ event: EVENTS.AI_CAP_HIT, properties: { cap: code } }];
  }
  return [];
}

const clean = (obj) => Object.fromEntries(Object.entries(obj || {}).filter(([, v]) => v !== undefined && v !== null));

